/**
 * useFileUpload Hook
 * Hook for file upload operations
 */

import { useState, useCallback } from 'react';
import { useFileService, useMultipartUploadService } from '@actschurch/ccms-sdk';
import { UploadQueueItem, UploadConfig } from '../types';
import { toast } from '@/components/ui/providers/AlertProvider';
import axios from 'axios';

export interface UseFileUploadReturn {
  // State
  queue: UploadQueueItem[];
  uploading: boolean;
  config: UploadConfig;
  completedCount: number;
  failedCount: number;

  // Actions
  setConfig: (config: Partial<UploadConfig>) => void;
  addToQueue: (files: File[]) => void;
  uploadFile: (queueItem: UploadQueueItem) => Promise<void>;
  uploadAll: () => Promise<void>;
  cancelUpload: (id: string) => void;
  retryUpload: (id: string) => Promise<void>;
  clearCompleted: () => void;
  clearQueue: () => void;
}

const generateQueueId = (): string => {
  return `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export const useFileUpload = (initialConfig?: UploadConfig): UseFileUploadReturn => {
  const fileService = useFileService();
  const multipartService = useMultipartUploadService();

  // State
  const [queue, setQueue] = useState<UploadQueueItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [config, setConfigState] = useState<UploadConfig>(
    initialConfig || {
      maxSize: 500 * 1024 * 1024, // 500MB
    }
  );
  const [completedCount, setCompletedCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);

  // Set config
  const setConfig = useCallback((newConfig: Partial<UploadConfig>) => {
    setConfigState((prev) => ({ ...prev, ...newConfig }));
  }, []);

  // Validate file
  const validateFile = useCallback(
    (file: File): string | null => {
      // Check file size
      if (config.maxSize && file.size > config.maxSize) {
        return `File size exceeds maximum limit of ${(config.maxSize / (1024 * 1024)).toFixed(0)}MB`;
      }

      // Check file type
      if (config.allowedTypes && config.allowedTypes.length > 0) {
        if (!config.allowedTypes.includes(file.type)) {
          return `File type ${file.type} is not allowed`;
        }
      }

      return null;
    },
    [config]
  );

  // Add files to queue
  const addToQueue = useCallback(
    (files: File[]) => {
      const newQueueItems: UploadQueueItem[] = files.map((file) => {
        const validationError = validateFile(file);

        return {
          id: generateQueueId(),
          file,
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
          domain: config.domain,
          recordId: config.recordId,
          folderPath: config.folderPath,
          folderId: config.folderId,
          status: validationError ? 'failed' : 'pending',
          progress: 0,
          error: validationError || undefined,
        };
      });

      setQueue((prev) => [...prev, ...newQueueItems]);

      // Show notifications for validation errors
      newQueueItems.forEach((item) => {
        if (item.status === 'failed' && item.error) {
          toast.error('Validation Error', `${item.fileName}: ${item.error}`);
        }
      });
    },
    [config, validateFile]
  );

  // Upload single file
  const uploadFile = useCallback(
    async (queueItem: UploadQueueItem) => {
      // Update status to uploading
      setQueue((prev) =>
        prev.map((item) =>
          item.id === queueItem.id
            ? { ...item, status: 'uploading', progress: 0, error: undefined }
            : item
        )
      );

      try {
        // Step 1: Get presigned URL
        const presignResponse = await fileService.presignUpload({
          fileName: queueItem.fileName,
          mimeType: queueItem.mimeType,
          domain: queueItem.domain,
          recordId: queueItem.recordId,
          folderPath: queueItem.folderPath,
          folderId: queueItem.folderId,
        });

        // Update queue with upload URL and file ID
        setQueue((prev) =>
          prev.map((item) =>
            item.id === queueItem.id
              ? {
                  ...item,
                  uploadUrl: presignResponse.uploadUrl,
                  finalUrl: presignResponse.finalUrl,
                  fileId: presignResponse.fileId,
                  progress: 10,
                }
              : item
          )
        );

        // Step 2: Upload to S3
        await axios.put(presignResponse.uploadUrl, queueItem.file, {
          headers: {
            'Content-Type': queueItem.mimeType,
          },
          onUploadProgress: (progressEvent: any) => {
            const percentCompleted = progressEvent.total
              ? Math.round(((progressEvent.loaded * 90) / progressEvent.total) + 10)
              : 50;

            setQueue((prev) =>
              prev.map((item) =>
                item.id === queueItem.id ? { ...item, progress: percentCompleted } : item
              )
            );
          },
        } as any);

        // Step 3: Confirm upload
        await fileService.confirmUpload({ fileId: presignResponse.fileId });

        // Update status to completed
        setQueue((prev) =>
          prev.map((item) =>
            item.id === queueItem.id ? { ...item, status: 'completed', progress: 100 } : item
          )
        );

        setCompletedCount((prev) => prev + 1);

        toast.success('Success', `${queueItem.fileName} uploaded successfully`);
      } catch (err: any) {
        const errorMessage = err.message || 'Upload failed';

        setQueue((prev) =>
          prev.map((item) =>
            item.id === queueItem.id
              ? { ...item, status: 'failed', error: errorMessage, progress: 0 }
              : item
          )
        );

        setFailedCount((prev) => prev + 1);

        toast.error('Upload Failed', `${queueItem.fileName}: ${errorMessage}`);
      }
    },
    [fileService, config]
  );

  // Upload all pending files
  const uploadAll = useCallback(async () => {
    setUploading(true);
    setCompletedCount(0);
    setFailedCount(0);

    const pendingItems = queue.filter((item) => item.status === 'pending');

    for (const item of pendingItems) {
      await uploadFile(item);
    }

    setUploading(false);

    const completed = queue.filter((item) => item.status === 'completed').length;
    const failed = queue.filter((item) => item.status === 'failed').length;

    if (failed === 0) {
      toast.success('All Uploads Complete', `${completed} file(s) uploaded successfully`);
    } else {
      toast.warning('Uploads Complete', `${completed} succeeded, ${failed} failed`);
    }
  }, [queue, uploadFile]);

  // Cancel upload
  const cancelUpload = useCallback((id: string) => {
    setQueue((prev) =>
      prev.map((item) =>
        item.id === id && item.status === 'uploading'
          ? { ...item, status: 'cancelled', error: 'Upload cancelled by user' }
          : item
      )
    );
  }, []);

  // Retry failed upload
  const retryUpload = useCallback(
    async (id: string) => {
      const item = queue.find((q) => q.id === id);
      if (!item || item.status !== 'failed') return;

      await uploadFile(item);
    },
    [queue, uploadFile]
  );

  // Clear completed uploads
  const clearCompleted = useCallback(() => {
    setQueue((prev) => prev.filter((item) => item.status !== 'completed'));
  }, []);

  // Clear entire queue
  const clearQueue = useCallback(() => {
    setQueue([]);
    setCompletedCount(0);
    setFailedCount(0);
  }, []);

  return {
    // State
    queue,
    uploading,
    config,
    completedCount,
    failedCount,

    // Actions
    setConfig,
    addToQueue,
    uploadFile,
    uploadAll,
    cancelUpload,
    retryUpload,
    clearCompleted,
    clearQueue,
  };
};
